import React, { useState, useEffect } from 'react';
import { Pagination, Spin } from 'antd';
import { 
  RightOutlined, 
  DownOutlined, 
  DollarOutlined,
  RiseOutlined,
  BarChartOutlined,
  FileTextOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { 
  RevenueForecastResponse, 
  BusinessUnit, 
  Department, 
  Client,
  RevenueCategory,
  QuarterData
} from '../../types/revenueForecast';
import { transformOldToNewStructure } from '../../utilities/revenueForecastTransformer';
import { ForecastRevenueResponse } from '../../store/reducers/revenueForecastSlice';
import './FinanceViewNew.css';

interface FinanceViewNewProps {
  data?: ForecastRevenueResponse | null;
  loading?: boolean;
  showConsolidatedView?: boolean;
  onOpenFilters?: () => void;
  onPageChange?: (page: number, pageSize: number) => void;
  currentPage?: number;
  pageSize?: number;
  totalElements?: number;
}

const FinanceViewNew: React.FC<FinanceViewNewProps> = ({ 
  data, 
  loading = false, 
  showConsolidatedView = false, 
  onOpenFilters,
  onPageChange,
  currentPage = 1,
  pageSize = 10,
  totalElements = 0
}) => {
  // State to hold transformed data
  const [forecastData, setForecastData] = useState<RevenueForecastResponse | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<RevenueCategory>('all');
  const [expandedBUs, setExpandedBUs] = useState<Set<string>>(new Set());
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedQuarters, setExpandedQuarters] = useState<Set<string>>(new Set()); // For quarter expansion
  const [isLoading, setIsLoading] = useState<boolean>(loading);

  // Transform data when API data changes
  useEffect(() => {
    console.log('FinanceViewNew - Raw API data received:', data);
    if (data) {
      const transformedData = transformOldToNewStructure(data);
      console.log('FinanceViewNew - Transformed data:', transformedData);
      setForecastData(transformedData);
    }
  }, [data]);

  // Sync loading state
  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toFixed(2)}`;
  };

  // Format percentage
  const formatPercentage = (actual: number, forecast: number): string => {
    if (forecast === 0) return '0%';
    return `${((actual / forecast) * 100).toFixed(1)}%`;
  };

  // Toggle expand/collapse
  const toggleBU = (buName: string) => {
    const newExpanded = new Set(expandedBUs);
    if (newExpanded.has(buName)) {
      newExpanded.delete(buName);
    } else {
      newExpanded.add(buName);
    }
    setExpandedBUs(newExpanded);
  };

  const toggleDept = (deptKey: string) => {
    const newExpanded = new Set(expandedDepts);
    if (newExpanded.has(deptKey)) {
      newExpanded.delete(deptKey);
    } else {
      newExpanded.add(deptKey);
    }
    setExpandedDepts(newExpanded);
  };

  const toggleClient = (clientKey: string) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientKey)) {
      newExpanded.delete(clientKey);
    } else {
      newExpanded.add(clientKey);
    }
    setExpandedClients(newExpanded);
  };

  const toggleQuarters = (key: string) => {
    const newExpanded = new Set(expandedQuarters);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedQuarters(newExpanded);
  };

  // Get all unique quarters
  const getAllQuarters = (): string[] => {
    if (!forecastData?.revenueForecast?.TotalRevenue?.quarters) return [];
    return forecastData.revenueForecast.TotalRevenue.quarters.map(q => q.quarter);
  };

  // Get filtered data based on selected quarter
  const getQuarterData = (quarters: QuarterData[], quarterName: string) => {
    if (quarterName === 'All') {
      return {
        projectRevenue: quarters.reduce((sum, q) => sum + q.projectRevenue, 0),
        pipelineRevenue: quarters.reduce((sum, q) => sum + q.pipelineRevenue, 0),
        total: quarters.reduce((sum, q) => sum + q.total, 0),
        actualRevenue: quarters.reduce((sum, q) => sum + q.actualRevenue, 0)
      };
    }
    const quarter = quarters.find(q => q.quarter === quarterName);
    return quarter || { projectRevenue: 0, pipelineRevenue: 0, total: 0, actualRevenue: 0 };
  };

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    if (!forecastData?.revenueForecast?.TotalRevenue) {
      return {
        totalForecast: 0,
        actualRevenue: 0,
        pipelineRevenue: 0,
        projectsRevenue: 0,
        realization: 0
      };
    }

    const total = forecastData.revenueForecast.TotalRevenue;
    
    if (selectedQuarter === 'All') {
      return {
        totalForecast: total.total,
        actualRevenue: total.actuals,
        pipelineRevenue: total.pipeline,
        projectsRevenue: total.projects,
        realization: total.total > 0 ? (total.actuals / total.total) * 100 : 0
      };
    }
    
    const quarterData = getQuarterData(total.quarters, selectedQuarter);

    return {
      totalForecast: quarterData.total || (quarterData.projectRevenue + quarterData.pipelineRevenue),
      actualRevenue: quarterData.actualRevenue,
      pipelineRevenue: quarterData.pipelineRevenue,
      projectsRevenue: quarterData.projectRevenue,
      realization: quarterData.total > 0 ? (quarterData.actualRevenue / quarterData.total) * 100 : 0
    };
  };

  const metrics = getSummaryMetrics();
  const quarters = getAllQuarters();

  // Pagination handler
  const handlePageChange = (page: number, size?: number) => {
    if (onPageChange) {
      onPageChange(page, size || pageSize);
    }
  };

  const allBUs = forecastData?.revenueForecast?.businessUnits || [];

  // Render Quarter Rows
  const renderQuarterRows = (quarters: QuarterData[], parentKey: string, indentClass: string) => {
    return quarters.map(quarter => (
      <tr key={`quarter-${parentKey}-${quarter.quarter}`} className={`quarter-row ${indentClass}`}>
        <td className="name-cell">
          <CalendarOutlined />
          <span className="quarter-text">{quarter.quarter}</span>
          {quarter.status === 'Current' && <span className="current-badge-inline">Current</span>}
        </td>
        <td className="amount-cell">{formatCurrency(quarter.projectRevenue)}</td>
        <td className="amount-cell">{formatCurrency(quarter.pipelineRevenue)}</td>
        <td className="amount-cell total-cell">{formatCurrency(quarter.total)}</td>
        <td className="amount-cell actuals-cell">{formatCurrency(quarter.actualRevenue)}</td>
      </tr>
    ));
  };

  // Render Business Unit Row
  const renderBURow = (bu: BusinessUnit) => {
    const isExpanded = expandedBUs.has(bu.name);
    const quartersExpanded = expandedQuarters.has(`bu-${bu.name}`);
    const quarterData = selectedQuarter === 'All' ? bu.totals : getQuarterData(bu.quarters, selectedQuarter);
    const quarterCount = bu.quarters.length;

    return (
      <>
        <tr key={`bu-${bu.name}`} className="bu-row">
          <td className="name-cell">
            <span onClick={() => toggleBU(bu.name)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
              <span className="bu-badge">BU</span>
              <span className="name-text">{bu.name}</span>
            </span>
            <span 
              className="quarter-badge clickable" 
              onClick={(e) => { e.stopPropagation(); toggleQuarters(`bu-${bu.name}`); }}
              style={{ cursor: 'pointer' }}
            >
              <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(quarterData.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(quarterData.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(quarterData.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(quarterData.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(bu.quarters, bu.name, 'bu-quarter-indent')}
        {isExpanded && bu.departments.map(dept => renderDeptRow(dept, bu.name))}
      </>
    );
  };

  // Render Department Row
  const renderDeptRow = (dept: Department, buName: string) => {
    const deptKey = `${buName}-${dept.name}`;
    const isExpanded = expandedDepts.has(deptKey);
    const quartersExpanded = expandedQuarters.has(`dept-${deptKey}`);
    const quarterData = selectedQuarter === 'All' ? dept.totals : getQuarterData(dept.quarters, selectedQuarter);
    const quarterCount = dept.quarters.length;

    return (
      <>
        <tr key={`dept-${deptKey}`} className="dept-row">
          <td className="name-cell dept-name">
            <span onClick={() => toggleDept(deptKey)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {dept.clients && dept.clients.length > 0 && (
                <>
                  {isExpanded ? <DownOutlined /> : <RightOutlined />}
                </>
              )}
              <span className="dept-badge">DEPT</span>
              <span className="name-text">{dept.name}</span>
            </span>
            <span 
              className="quarter-badge clickable" 
              onClick={(e) => { e.stopPropagation(); toggleQuarters(`dept-${deptKey}`); }}
              style={{ cursor: 'pointer' }}
            >
              <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(quarterData.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(quarterData.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(quarterData.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(quarterData.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(dept.quarters, deptKey, 'dept-quarter-indent')}
        {isExpanded && dept.clients?.map(client => renderClientRow(client, deptKey))}
      </>
    );
  };

  // Render Client Row
  const renderClientRow = (client: Client, deptKey: string) => {
    const clientKey = `${deptKey}-${client.name}`;
    const quartersExpanded = expandedQuarters.has(`client-${clientKey}`);
    const quarterData = selectedQuarter === 'All' ? client.totals : getQuarterData(client.quarters, selectedQuarter);
    const quarterCount = client.quarters.length;

    return (
      <>
        <tr key={`client-${clientKey}`} className="client-row">
          <td className="name-cell client-name">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              <span className="client-badge">CLIENT</span>
              <span className="name-text">{client.name}</span>
            </span>
            <span 
              className="quarter-badge clickable" 
              onClick={(e) => { e.stopPropagation(); toggleQuarters(`client-${clientKey}`); }}
              style={{ cursor: 'pointer' }}
            >
              <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(quarterData.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(quarterData.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(quarterData.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(quarterData.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(client.quarters, clientKey, 'client-quarter-indent')}
      </>
    );
  };

  if (isLoading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (!forecastData) {
    return (
      <div className="no-data-container">
        <p>No revenue forecast data available.</p>
      </div>
    );
  }

  return (
    <div className="finance-view-new">
      {/* Quarter Tabs */}
      <div className="quarter-section">
        <div className="quarter-tabs">
          <button 
            className={`quarter-tab ${selectedQuarter === 'All' ? 'active' : ''}`}
            onClick={() => setSelectedQuarter('All')}
          >
            All
          </button>
          {quarters.map(quarter => {
            const isCurrentQuarter = forecastData?.revenueForecast?.TotalRevenue?.quarters
              .find(q => q.quarter === quarter)?.status === 'Current';
            
            return (
              <button
                key={quarter}
                className={`quarter-tab ${selectedQuarter === quarter ? 'active' : ''} ${isCurrentQuarter ? 'current' : ''}`}
                onClick={() => setSelectedQuarter(quarter)}
              >
                {quarter}
                {isCurrentQuarter && <span className="current-badge">Current</span>}
              </button>
            );
          })}
        </div>

        <div className="quarter-summary">
          <div className="summary-item">
            <span className="summary-label">Forecast:</span>
            <span className="summary-value">{formatCurrency(metrics.totalForecast)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Actuals:</span>
            <span className="summary-value green">{formatCurrency(metrics.actualRevenue)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Realization:</span>
            <span className="summary-value">{metrics.realization.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Revenue Forecast Table */}
      <div className="revenue-forecast-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0 }}>Revenue Forecast</h3>
            <span style={{ color: '#6b7280', fontSize: '14px', fontWeight: 500 }}>
              <FileTextOutlined /> {totalElements || 0} Business Units
            </span>
          </div>
          {onOpenFilters && (
            <button
              onClick={onOpenFilters}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                height: '44px',
                paddingLeft: '24px',
                paddingRight: '24px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                fontWeight: 500,
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 1024 1024" fill="currentColor">
                <path d="M880.1 154H143.9c-24.5 0-39.8 26.7-27.5 48L349 597.4V838c0 17.7 14.2 32 31.8 32h262.4c17.6 0 31.8-14.3 31.8-32V597.4L907.7 202c12.2-21.3-3.1-48-27.6-48zM603.4 798H420.6V642h182.9v156zm9.6-236.6l-9.5 16.6h-183l-9.5-16.6L212.7 226h598.6L613 561.4z"/>
              </svg>
              Open Filters
            </button>
          )}
        </div>

        <div className="table-container" style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <Spin size="large" tip="Loading data..." />
            </div>
          )}
          <table className="revenue-table">
            <thead>
              <tr>
                <th className="name-header">BUSINESS UNIT / DEPT / CLIENT</th>
                <th className="amount-header">PROJECTS</th>
                <th className="amount-header">PIPELINE</th>
                <th className="amount-header">TOTAL</th>
                <th className="amount-header">ACTUALS</th>
              </tr>
            </thead>
            <tbody>
              {allBUs.map(bu => renderBURow(bu))}
            </tbody>
            <tfoot>
              {/* Total Row */}
              <tr className="total-row">
                <td className="name-cell">
                  <strong>Total Revenue</strong>
                  <span 
                    className="quarter-badge clickable"
                    onClick={() => toggleQuarters('total-revenue')}
                    style={{ cursor: 'pointer' }}
                  >
                    <CalendarOutlined /> {expandedQuarters.has('total-revenue') ? 'Hide Qtrs' : `${quarters.length} Qtrs`}
                  </span>
                </td>
                <td className="amount-cell"><strong>{formatCurrency(metrics.projectsRevenue)}</strong></td>
                <td className="amount-cell"><strong>{formatCurrency(metrics.pipelineRevenue)}</strong></td>
                <td className="amount-cell total-cell"><strong>{formatCurrency(metrics.totalForecast)}</strong></td>
                <td className="amount-cell actuals-cell"><strong>{formatCurrency(metrics.actualRevenue)}</strong></td>
              </tr>
              {expandedQuarters.has('total-revenue') && forecastData?.revenueForecast?.TotalRevenue?.quarters && 
                renderQuarterRows(forecastData.revenueForecast.TotalRevenue.quarters, 'total', 'total-quarter-indent')
              }
            </tfoot>
          </table>
        </div>
        
        {/* Pagination */}
        {totalElements > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', marginBottom: '16px', background: 'white', padding: '16px', borderRadius: '8px' }}>
            <Pagination
              current={currentPage}
              total={totalElements}
              pageSize={pageSize}
              onChange={handlePageChange}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceViewNew;
